import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { AngularFireAuth } from '@angular/fire/auth';
import { AuthService } from '../services/auth.service';
import { DataModelService } from '../services/data-model.service';
import { InboxNotification } from '../services/definitions.service';

@Component({
  selector: 'app-notification-inbox',
  templateUrl: './notification-inbox.component.html',
  styleUrls: ['./notification-inbox.component.css'],
})
export class NotificationInboxComponent implements OnInit, OnDestroy {
  notifications: InboxNotification[] = [];
  selectedNotification: InboxNotification | null = null;
  isLoading = true;
  isLoadingDetail = false;

  // Firestore document ID of the notification the user clicked, until auto-selected.
  private pendingId: string | null = null;
  private sub: Subscription;
  private paramSub: Subscription;

  constructor(
    private dataModel: DataModelService,
    private authService: AuthService,
    private afAuth: AngularFireAuth,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    // Wait for Firebase auth to resolve before subscribing to Firestore.
    // When the app opens cold from a notification click, userId may not be
    // set yet in AuthService — authState emits once auth resolves.
    this.afAuth.authState
      .pipe(filter(user => !!user), take(1))
      .subscribe(() => {
        const userId = this.authService.userId;
        if (userId) {
          this.sub = this.dataModel
            .subscribeToInboxNotifications(userId)
            .subscribe((notifications) => {
              this.notifications = notifications;
              this.isLoading = false;
              this.tryOpenPending();
            });
        }
      });

    // Use observable (not snapshot) so this fires when the service worker navigates
    // an already-open window to /notifications?id=xxx without re-creating the component.
    this.paramSub = this.route.queryParams.subscribe((params) => {
      const id = params['id'];
      if (id) {
        this.pendingId = id;
        this.isLoadingDetail = true;
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {},
          replaceUrl: true,
        });
        this.tryOpenPending();
      }
    });
  }

  private tryOpenPending(): void {
    if (!this.pendingId || this.selectedNotification) return;
    const match = this.notifications.find((entry) => entry.id === this.pendingId);
    if (match) {
      this.openNotification(match);
      this.pendingId = null;
      this.isLoadingDetail = false;
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.paramSub?.unsubscribe();
  }

  openNotification(notification: InboxNotification) {
    this.selectedNotification = notification;
    if (!notification.read) {
      this.dataModel.markNotificationRead(this.authService.userId, notification.id);
    }
  }

  closeDetail() {
    this.selectedNotification = null;
    this.isLoadingDetail = false;
    this.pendingId = null;
  }

  markSelectedUnread() {
    if (!this.selectedNotification) return;
    this.dataModel.markNotificationUnread(this.authService.userId, this.selectedNotification.id);
    this.closeDetail();
  }

  deleteSelected() {
    if (!this.selectedNotification) return;
    this.dataModel.deleteNotification(this.authService.userId, this.selectedNotification.id);
    this.closeDetail();
  }

  markAllRead() {
    const userId = this.authService.userId;
    this.notifications
      .filter((n) => !n.read)
      .forEach((n) => this.dataModel.markNotificationRead(userId, n.id));
  }

  formatTimestamp(notification: InboxNotification): string {
    if (!notification.timestamp) return '';
    const date = notification.timestamp.toDate();
    return date.toLocaleDateString('de-DE') + ' ' + date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  }

  get pendingIdDisplay(): string {
    return this.pendingId || '';
  }

  get unreadCount(): number {
    return this.notifications.filter((n) => !n.read).length;
  }
}
